import React from "react";
import PolicyLayout, { Section } from "@/components/PolicyLayout";

const SECTIONS = [
  { id: "intro", label: "1. Overview" },
  { id: "when", label: "2. When You Are Entitled to a Refund" },
  { id: "modes", label: "3. Modes of Refund" },
  { id: "tat", label: "4. Refund Timelines (RBI-aligned)" },
  { id: "partial", label: "5. Partial Refunds" },
  { id: "cod", label: "6. Cash on Delivery Refunds" },
  { id: "failed", label: "7. Failed Transactions" },
  { id: "chargeback", label: "8. Chargebacks & Unauthorised Transactions" },
  { id: "trace", label: "9. How to Track Your Refund" },
  { id: "grievance", label: "10. Grievance" },
];

export default function RefundPolicy() {
  return (
    <PolicyLayout
      title="Refund Policy"
      tagline="Refunds are processed strictly in accordance with the Reserve Bank of India (RBI) Turn-Around-Time (TAT) framework and our Payment Aggregator's settlement cycles."
      updated="February 2026"
      sections={SECTIONS}
    >
      <Section id="intro" title="1. Overview">
        <p>
          This Refund Policy sets out how Florvanta Commerce Pvt Ltd processes refunds for orders placed on the Platform. It should be read together with the <a className="text-[#14A085] underline" href="/return-policy">Return Policy</a> and the <a className="text-[#14A085] underline" href="/terms">Terms &amp; Conditions</a>.
        </p>
      </Section>

      <Section id="when" title="2. When You Are Entitled to a Refund">
        <ul className="list-disc pl-5 space-y-1">
          <li>Order cancelled by you before shipment.</li>
          <li>Order cancelled by the Vendor / Florvanta (out of stock, non-serviceable pincode, pricing error, KYC failure).</li>
          <li>Product returned and accepted after QC, per the Return Policy.</li>
          <li>Damaged, defective, wrong, or missing item reported within the timelines specified in the Return Policy.</li>
          <li>Failed online transaction where the amount was debited but the order was not confirmed.</li>
        </ul>
      </Section>

      <Section id="modes" title="3. Modes of Refund">
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Online payment (UPI / Cards / Net-banking / Wallet):</b> refunded to the original source account through the Payment Aggregator.</li>
          <li><b>Cash on Delivery (COD):</b> refunded to a bank account of your choice through NEFT / IMPS after verification (see Section 6).</li>
          <li>Refunds are made in Indian Rupees (INR) only, at the invoice value; foreign-exchange fluctuations are not compensated.</li>
        </ul>
      </Section>

      <Section id="tat" title="4. Refund Timelines — RBI TAT Framework">
        <p>
          Refund timelines follow the <b>RBI Circular DPSS.CO.PD No.629/02.01.014/2019-20 dated 20 September 2019 on Harmonisation of Turn Around Time (TAT) and Customer Compensation for Failed Transactions</b>, as amended.
        </p>
        <div className="overflow-auto rounded-lg border border-[#E2E8E5]">
          <table className="w-full text-sm">
            <thead className="bg-[#E8F5F2] text-[#0F3D2E]">
              <tr>
                <th className="p-3 text-left">Scenario</th>
                <th className="p-3 text-left">Refund Initiation</th>
                <th className="p-3 text-left">Credit to Account</th>
              </tr>
            </thead>
            <tbody className="text-[#0F3D2E]">
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Order cancelled before shipment</td>
                <td className="p-3">Same day</td>
                <td className="p-3">5-7 working days</td>
              </tr>
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Return approved after QC</td>
                <td className="p-3">Within 2 working days of QC clearance</td>
                <td className="p-3">5-7 working days</td>
              </tr>
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">UPI failed transaction (amount debited, order not placed)</td>
                <td className="p-3">Auto-reversal by acquiring bank</td>
                <td className="p-3">T+1 working day (per RBI TAT)</td>
              </tr>
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Card failed transaction (amount debited, order not placed)</td>
                <td className="p-3">Auto-reversal by issuing bank</td>
                <td className="p-3">T+5 working days (per RBI TAT)</td>
              </tr>
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Net-banking failed transaction</td>
                <td className="p-3">Auto-reversal by bank</td>
                <td className="p-3">T+3 working days (per RBI TAT)</td>
              </tr>
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Wallet / PPI failed transaction</td>
                <td className="p-3">Auto-reversal by PPI issuer</td>
                <td className="p-3">T+1 working day (per RBI TAT)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#6E877C]">
          &quot;T&quot; = date of the transaction. &quot;Working day&quot; excludes bank holidays. Timelines beyond RBI TAT attract customer compensation of ₹100 per calendar day of delay, as prescribed in the RBI Circular.
        </p>
      </Section>

      <Section id="partial" title="5. Partial Refunds">
        <p>
          If only a portion of the order is returned, the refund will be for the returned line items less any category-specific shipping fee that was originally borne by the Vendor. Commission collected by Florvanta is refunded proportionately.
        </p>
      </Section>

      <Section id="cod" title="6. Cash on Delivery Refunds">
        <p>
          For COD orders, you will be asked to provide bank account details (account number, IFSC, holder name) through a secure form. Refunds are made via NEFT / IMPS to that account only. For fraud prevention, the account holder&apos;s name must reasonably match the registered customer name.
        </p>
      </Section>

      <Section id="failed" title="7. Failed Transactions">
        <p>
          If your account was debited but no order was created on the Platform, please wait for the RBI TAT above. If credit is not received within TAT, raise a ticket via <b>support@florvanta.co.in</b> with the UTR / RRN / transaction reference and your registered mobile number. Compensation, if applicable under the RBI TAT circular, will be credited automatically by your issuing bank.
        </p>
      </Section>

      <Section id="chargeback" title="8. Chargebacks &amp; Unauthorised Transactions">
        <p>
          If you notice an unauthorised transaction on your card or account, please immediately (i) block your card/UPI with your bank, and (ii) notify us at <b>grievance@florvanta.co.in</b> within three (3) working days as per the <b>RBI Circular on Limiting Liability of Customers in Unauthorised Electronic Banking Transactions</b> (RBI/2017-18/15) so that we can co-operate with your bank on the chargeback.
        </p>
      </Section>

      <Section id="trace" title="9. How to Track Your Refund">
        <p>
          Refund status is visible under <b>My Orders → Order details → Refund status</b> and via email/SMS. For online refunds, the ARN (Acquirer Reference Number) will be shared once initiated; use it with your bank to trace credit.
        </p>
      </Section>

      <Section id="grievance" title="10. Grievance">
        <p>
          Unresolved refund complaints beyond 15 days may be escalated to <b>grievance@florvanta.co.in</b>. If not addressed to your satisfaction thereafter, you may approach the RBI&apos;s <b>Complaint Management System (CMS)</b> for issues relating to online payments and the RBI Ombudsman under the Integrated Ombudsman Scheme, 2021.
        </p>
      </Section>
    </PolicyLayout>
  );
}
